import {
  BMEEmailTemplateName,
  ChannelsType,
  DonorGender,
  DonorPaymentMethod,
  DonorSPHOrGeneralPubic,
  KIT_SAMPLE_TYPE,
  KitFtpType,
  KitStatusExpand,
  KitStatus,
  PDF_QC,
  REPORT_STATUS,
  WorkflowTimeline,
} from '@/enums';
import { OrderItemPayload, OrderPayload } from './order';
import { ChannelEntity, ProjectEntity } from './project';
import { UserEntity } from './user';
import { ExtractionSequenceEntity } from './extractionBatch';
import { SequenceEntity } from './sequence';
import { IFtpDonorSample } from './ftp';
import { TenantEntity } from './tenant';
import { ProfilePayload } from './profile';
import { KitAttributes, SampleEntity } from '@/types';
import {
  KitStatusHistoryEntity,
  PiiPatientDetail,
} from '@/pages/privatePages/kit/types';
import { IReportBugBase } from './report';
import { Dayjs } from 'dayjs';

export type KitTabType = ChannelsType | 'DELETED' | 'ALL';

export interface StatusHistory {
  id: string;
  userId: string;
  status: KitStatus;
  createdAt: Date | string | null;
  kidId: string;
}

export interface MetaTag {
  id: string;
  resourceId: string;
  key: string;
  value: string;
  entity: string;
  displayValue: string;
}

export interface KitPayload {
  image?: string;
  projectId?: string;
  project: ProjectEntity;
  tenant?: TenantEntity;
  profile?: ProfilePayload;
  status: KitStatus | string;
  orderType?: string;
  notes?: string | null;
  // activated: boolean;
  // activatedAt?: Date | string | null;
  serial: string;
  id: string;
  qrCode?: string;
  order?: OrderPayload;
  quantityOfSamples?: number;
  isUnassigned?: boolean;
  orderItem?: OrderItemPayload;
  b2bOrderItem?: OrderItemPayload;
  type?: string;
  // kitSendOut?: boolean;
  quantityOfDnaSamples?: number;
  // kitReceivedAt?: Date | string | null;
  // kitSendOutAt?: Date | string | null;
  statusHistory?: StatusHistory[];
  isFfqSurveyCompletion?: boolean;
  latestSequence?: SequenceEntity;
  activeSequence?: SequenceEntity;
  labId?: string;
  samples?: SampleEntity[];
  // completedAt?: Date | string | null;
  reportBugbase?: IReportBugBase;
  pdfFileName?: string;
  b2dProfileName?: string;
  isV1?: boolean;
  tags?: string[];
  pdfPath?: string;
  pdfPublish?: boolean;
  timePoint?: number;
  ftpType?: KitFtpType;
  ftpDonorSample?: IFtpDonorSample;
  qualtricsResponseId?: string;
  surveyStartAt?: Date;
  isAliquoted?: boolean;
  channel?: ChannelEntity;
  b2dProfileNameData?: B2DProfileDataPayload;
  labIdHistory?: LabHistory[];
  analysisId?: string;
  sampleReceivedAt?: string;
  createdAt?: string | Date;
  extractionSequences?: ExtractionSequenceEntity[];
  metaTags?: MetaTag[];
  externalId?: string;
  // isPdfReportSuccess: boolean;
  pdfQC?: PDF_QC;
  reportStatus: REPORT_STATUS;
  attributes?: KitAttributes[];
  mappedKits?: string[];
  isDelayed?: boolean;
  currentStatus?: KitStatusHistoryEntity;
  piiPatientDetail?: PiiPatientDetail;
  allowDnaExtraction?: boolean;
}

export interface LabHistory {
  id?: string;
  createdAt?: Date | string;
  deletedAt?: Date | string;
  updatedAt?: Date | string;
  kitId?: string;
  labId?: string;
}

// export interface ActivateKitPayload {
//   profileId: string;
//   id?: string | null;
// }

export interface PayloadGenerateKitSerials {
  quantity: number;
  prefix?: string;
  projectId?: string;
}

export interface PayloadMarkKitSerialsPrinted {
  ids: string[];
}

export interface Sample {
  id: string;
  sampleId: string;
  kitId: string;
  location: string;
  dataMatrix: string;
  locationAddUser?: UserEntity;
  timeAddLocation?: string;
  sort: number;
  type: string;
  extractionSequences?: ExtractionSequenceEntity[];
}

export const STATUS_BASED_ON_TIMELINE: {
  [props in WorkflowTimeline]: (KitStatus | KitStatusExpand)[];
} = {
  [WorkflowTimeline.AMD]: [
    KitStatus.UNACTIVATED,
    KitStatus.SENT_OUT,
    KitStatus.KIT_RECEIVED,
    KitStatus.DNA_EXTRACTED,
    KitStatus.SEQUENCING,
    KitStatus.COMPLETED,
    KitStatusExpand.REPORT_READY,
  ],
  [WorkflowTimeline.BME]: [
    KitStatus.UNACTIVATED,
    KitStatus.KIT_RECEIVED,
    KitStatus.DNA_EXTRACTED,
    KitStatus.SEQUENCING,
    KitStatus.COMPLETED,
    KitStatusExpand.REPORT_READY,
  ],
  [WorkflowTimeline.FFH]: [
    KitStatus.UNACTIVATED,
    KitStatus.SENT_OUT,
    KitStatus.KIT_RECEIVED,
    KitStatus.DNA_EXTRACTED,
    KitStatus.SEQUENCING,
    KitStatus.COMPLETED,
    KitStatusExpand.REPORT_READY,
  ],
  [WorkflowTimeline.FMT]: [
    KitStatus.UNACTIVATED,
    KitStatus.SENT_OUT,
    KitStatus.KIT_RECEIVED,
    KitStatus.DNA_EXTRACTED,
    KitStatus.SEQUENCING,
    KitStatus.COMPLETED,
    KitStatusExpand.REPORT_READY,
  ],
  [WorkflowTimeline.RESEARCH]: [
    KitStatus.UNACTIVATED,
    KitStatus.KIT_RECEIVED,
    KitStatus.ALIQUOTED,
    KitStatus.DNA_EXTRACTED,
    KitStatus.SEQUENCING,
    KitStatus.COMPLETED,
    KitStatusExpand.REPORT_READY,
  ],
  [WorkflowTimeline.EXTERNAL]: [
    KitStatus.UNACTIVATED,
    KitStatus.KIT_RECEIVED,
    KitStatus.DNA_EXTRACTED,
    KitStatus.SEQUENCING,
    KitStatus.UNDER_SUPPORT,
    KitStatus.COMPLETED,
    KitStatusExpand.REPORT_READY,
  ],
};

export const KIT_STATUS_FLOW = {
  [WorkflowTimeline.AMD]: [
    [KitStatus.UNACTIVATED],
    [KitStatus.SENT_OUT],
    [KitStatus.KIT_RECEIVED],
    [KitStatus.DNA_EXTRACTED, KitStatus.FFQ_SURVEY_COMPLETED],
    [KitStatus.SEQUENCING],
    [KitStatus.COMPLETED],
  ],
  [WorkflowTimeline.BME]: [
    [KitStatus.UNACTIVATED, KitStatus.ACTIVATED],
    [KitStatus.KIT_RECEIVED],
    [KitStatus.DNA_EXTRACTED],
    [KitStatus.SEQUENCING],
    [KitStatus.COMPLETED],
    [KitStatus.REPORT_READY],
    // [KitStatus.COMPLETED, KitStatus.UNDER_SUPPORT],
    // [KitStatus.UNDER_SUPPORT],
  ],
  [WorkflowTimeline.FFH]: [
    [KitStatus.UNACTIVATED],
    [KitStatus.SENT_OUT],
    [KitStatus.KIT_RECEIVED],
    [KitStatus.DNA_EXTRACTED],
    [KitStatus.SEQUENCING],
    [KitStatus.COMPLETED],
  ],
  [WorkflowTimeline.RESEARCH]: [
    [KitStatus.UNACTIVATED],
    [KitStatus.KIT_RECEIVED],
    [KitStatus.ALIQUOTED],
    [KitStatus.DNA_EXTRACTED],
    [KitStatus.SEQUENCING],
    [KitStatus.COMPLETED],
  ],
  [WorkflowTimeline.FMT]: [
    [KitStatus.UNACTIVATED],
    [KitStatus.SENT_OUT],
    [KitStatus.KIT_RECEIVED],
    [KitStatus.DNA_EXTRACTED],
    [KitStatus.SEQUENCING],
    [KitStatus.COMPLETED],
  ],
  [WorkflowTimeline.EXTERNAL]: [
    [KitStatus.UNACTIVATED],
    [KitStatus.KIT_RECEIVED],
    [KitStatus.DNA_EXTRACTED],
    [KitStatus.SEQUENCING],
    [KitStatus.COMPLETED],
    // [KitStatus.UNDER_SUPPORT],
    // [KitStatus.REPORT_READY],
  ],
};

export const TIMELINE_BASED_ON_WORKFLOW: {
  [keys in WorkflowTimeline]: any;
} = {
  [WorkflowTimeline.AMD]: {
    [KitStatus.UNACTIVATED]: [KitStatus.SENT_OUT],
    [KitStatus.SENT_OUT]: [KitStatus.KIT_RECEIVED],
    [KitStatus.KIT_RECEIVED]: [
      KitStatus.DNA_EXTRACTED,
      KitStatus.FFQ_SURVEY_COMPLETED,
    ],
    [KitStatus.DNA_EXTRACTED]: [
      KitStatus.SEQUENCING,
      KitStatus.FFQ_SURVEY_COMPLETED,
    ],
    [KitStatus.SEQUENCING]: [
      KitStatus.COMPLETED,
      KitStatus.FFQ_SURVEY_COMPLETED,
    ],
  },
  [WorkflowTimeline.BME]: {
    [KitStatus.UNACTIVATED]: [KitStatus.KIT_RECEIVED],
    [KitStatus.ACTIVATED]: [KitStatus.KIT_RECEIVED],
    [KitStatus.KIT_RECEIVED]: [KitStatus.DNA_EXTRACTED],
    [KitStatus.DNA_EXTRACTED]: [KitStatus.SEQUENCING],
    [KitStatus.SEQUENCING]: [KitStatus.COMPLETED],
  },
  [WorkflowTimeline.FFH]: {
    [KitStatus.UNACTIVATED]: [KitStatus.SENT_OUT],
    [KitStatus.SENT_OUT]: [KitStatus.KIT_RECEIVED],
    [KitStatus.KIT_RECEIVED]: [KitStatus.DNA_EXTRACTED],
    [KitStatus.DNA_EXTRACTED]: [KitStatus.SEQUENCING],
    [KitStatus.SEQUENCING]: [KitStatus.COMPLETED],
  },
  [WorkflowTimeline.RESEARCH]: {
    [KitStatus.UNACTIVATED]: [KitStatus.KIT_RECEIVED],
    [KitStatus.KIT_RECEIVED]: [KitStatus.ALIQUOTED],
    [KitStatus.ALIQUOTED]: [KitStatus.DNA_EXTRACTED],
    [KitStatus.DNA_EXTRACTED]: [KitStatus.SEQUENCING],
    [KitStatus.SEQUENCING]: [KitStatus.COMPLETED],
  },
  [WorkflowTimeline.FMT]: {
    [KitStatus.UNACTIVATED]: [KitStatus.SENT_OUT],
    [KitStatus.SENT_OUT]: [KitStatus.KIT_RECEIVED],
    [KitStatus.KIT_RECEIVED]: [KitStatus.DNA_EXTRACTED],
    [KitStatus.DNA_EXTRACTED]: [KitStatus.SEQUENCING],
    [KitStatus.SEQUENCING]: [KitStatus.COMPLETED],
  },
  [WorkflowTimeline.EXTERNAL]: {
    [KitStatus.UNACTIVATED]: [KitStatus.KIT_RECEIVED],
    [KitStatus.ACTIVATED]: [KitStatus.KIT_RECEIVED],
    [KitStatus.KIT_RECEIVED]: [KitStatus.DNA_EXTRACTED],
    [KitStatus.DNA_EXTRACTED]: [KitStatus.SEQUENCING],
    [KitStatus.SEQUENCING]: [KitStatus.UNDER_SUPPORT],
    [KitStatus.UNDER_SUPPORT]: [KitStatus.COMPLETED],
  },
};

export interface IExternalParticipantForm {
  kitSerial: string;
  externalParticipantId?: string;
  emailAddress: string;
  firstName: string;
  lastName: string;
  gender?: DonorGender;
  mailingAddress?: string;
  contactNumber?: string;
  paymentMethod?: DonorPaymentMethod;
  // bankName?: string;
  // accountNumber?: string;
  sphOrGeneralPublic?: DonorSPHOrGeneralPubic;
  remark?: string;
  isAMD?: boolean;
  allowDnaExtraction?: boolean;
}

export enum DonorEmailTemplateEnum {
  ASK_DONOR_COMPLETE_FFQ = 'ASK_DONOR_COMPLETE_FFQ',
  NOTICE_DONOR_FFQ_COMPLETE = 'NOTICE_DONOR_FFQ_COMPLETE',
  REMIND_DONOR_SEND_OUT_KIT = 'REMIND_DONOR_SEND_OUT_KIT',
}

export interface IPayloadSendDonorEmail {
  kitSerials: string[];
  templateHtml: string | undefined;
  templateType?: DonorEmailTemplateEnum;
}

export interface IPayloadSendBMEEmail {
  kitSerials: string[];
  templateHTML: string | undefined;
  templateType?: BMEEmailTemplateName;
}

export const DELETE_TYPE = {
  DELETED_AND_NONE: 'deleted-and-not-deleted' as 'deleted-and-not-deleted',
  NONE_DEL: 'not-delete' as 'not-delete',
};

export interface FilteredSamples {
  [KIT_SAMPLE_TYPE.DNA]: {
    [DELETE_TYPE.DELETED_AND_NONE]: SampleEntity[];
    [DELETE_TYPE.NONE_DEL]: SampleEntity[];
  };
  [KIT_SAMPLE_TYPE.NORMAL]: {
    [DELETE_TYPE.DELETED_AND_NONE]: SampleEntity[];
    [DELETE_TYPE.NONE_DEL]: SampleEntity[];
  };
}

export interface MarkKitReceived {
  kitSerials: string[];
  expectDate: Date;
}
export interface B2DProfileDataPayload {
  firstName: string;
  lastName: string;
  fullName?: string;
  birthdate?: string | Dayjs;
}
